/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   08/11/2022
 */

import React from "react";
import {
	Checkbox,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid,
	InputLabel,
	MenuItem,
	Select
} from "@mui/material";
import { UseFormReturnType } from "@mantine/form";
import { CalendarSettingsForm } from "./CalendarSettingsDialog";

export interface CalendarSettingsCapacityProps {
	form: UseFormReturnType<CalendarSettingsForm>;
}

export default function CalendarSettingsCapacity({ form }: CalendarSettingsCapacityProps) {

	const capacityDisabled = form.values.settings.capacity === "off";

	return (
		<Grid container spacing={2}>
			<Grid item xs={6}>
				<FormControl variant={"standard"} fullWidth>
					<InputLabel>Show Capacity As</InputLabel>
					<Select
						{...form.getInputProps("settings.capacity")}
						onChange={(event) => form.setFieldValue("settings.capacity", event.target.value)}
					>
						<MenuItem value={"availability"}>Availability</MenuItem>
						<MenuItem value={"headcount"}>Headcount</MenuItem>
						<MenuItem value={"availability-headcount"}>Availability and headcount</MenuItem>
						<MenuItem value={"headcount-availability"}>Headcount and availability</MenuItem>
						<MenuItem value={"off"}>Disabled</MenuItem>
					</Select>
				</FormControl>
			</Grid>
			<Grid item xs={6}>
				<FormControl variant={"standard"} disabled={capacityDisabled} fullWidth>
					<InputLabel>Coloring Threshold</InputLabel>
					<Select
						{...form.getInputProps("settings.capacityThreshold")}
						onChange={(event) => form.setFieldValue("settings.capacityThreshold", event.target.value)}
					>
						{[ "50", "60", "70", "80", "90", "100" ].map(t =>
							<MenuItem key={t} value={t}>{t + "%"}</MenuItem>)
						}
					</Select>
					<FormHelperText>
						Capacity values below the indicated threshold will be color-coded.
					</FormHelperText>
				</FormControl>
			</Grid>
			<Grid item xs={6}>
				<FormControl variant={"standard"} fullWidth disabled={capacityDisabled}>
					<FormControlLabel
						control={
							<Checkbox
								{...form.getInputProps("settings.tentativeEventsInCapacity", { type: "checkbox" })}
							/>
						}
						label="Tentative events affect capacity"
					/>
					<FormHelperText>
						When unchecked, unconfirmed events will not be included in the capacity calculation.
					</FormHelperText>
				</FormControl>
			</Grid>
			<Grid item xs={6}>
				<FormControl variant={"standard"} fullWidth disabled={capacityDisabled}>
					<FormControlLabel
						control={
							<Checkbox
								{...form.getInputProps("settings.fractionalCapacityEnabled", { type: "checkbox" })}
							/>
						}
						label="Enable fractional capacity"
					/>
					<FormHelperText>
						When unchecked, events with capacity less than 100% will be calculated as if 0%
					</FormHelperText>
				</FormControl>
			</Grid>
			<Grid item xs={6}>
				<FormControl variant={"standard"} fullWidth disabled={capacityDisabled}>
					<FormControlLabel
						control={
							<Checkbox
								{...form.getInputProps("settings.groupCapacityEnabled", { type: "checkbox" })}
							/>
						}
						label={"Show capacity in groups"}
					/>
					<FormHelperText>
						When unchecked, capacity will be shown for all groups combined.
					</FormHelperText>
				</FormControl>
			</Grid>
		</Grid>
	);

}